import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useGraphQL } from '../graphql';
import { useJobSchemas, resolveMutationKey } from '../hooks';
import Modal from './Modal';
import Editor from '@monaco-editor/react';
import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ClockIcon,
    DocumentDuplicateIcon,
    TrashIcon,
    ChevronDownIcon,
} from '@heroicons/react/24/outline';

const CreateJobModal = ({ isOpen, onClose, onJobCreated }) => {
    const { executeMutation, executeQuery, queries, mutations, loading: mutationLoading } = useGraphQL();
    const {
        jobTypes,
        jobTypesByCategory,
        categories,
        loading: schemasLoading,
        error: schemasError,
        getJobType,
        getParametersPlaceholder,
        getFullParameterTemplate,
        buildVariables,
        goTypeToJSType,
        getParamsHistoryForJob,
        saveParamsToHistory,
        clearParamsHistoryForJob,
    } = useJobSchemas();

    const [formData, setFormData] = useState({
        type: '',
        tenderId: '',
        documentId: '',
        parameters: '{}',
        cronSchedule: ''
    });
    const [error, setError] = useState('');
    const [isScheduled, setIsScheduled] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [users, setUsers] = useState([]);
    const [showHistoryDropdown, setShowHistoryDropdown] = useState(false);

    // Monaco editor refs
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const completionProviderRef = useRef(null);
    const companiesRef = useRef([]);
    const usersRef = useRef([]);

    // Keep refs in sync with state for Monaco completion provider
    useEffect(() => {
        companiesRef.current = companies;
    }, [companies]);

    useEffect(() => {
        usersRef.current = users;
    }, [users]);

    const loading = mutationLoading || schemasLoading;

    // Load companies and users for jobs requiring selection and for autocomplete
    useEffect(() => {
        if (isOpen) {
            loadCompanies();
            loadUsers();
        }
    }, [isOpen]);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 200, offset: 0 });
            setCompanies(data.adminCompanies || []);
            if (data.adminCompanies?.length > 0 && !selectedCompanyId) {
                setSelectedCompanyId(data.adminCompanies[0].id);
            }
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadUsers = async () => {
        try {
            const data = await executeQuery(queries.getUsers(), { limit: 200, offset: 0 });
            setUsers(data.adminUsers || []);
        } catch (err) {
            console.error('Failed to load users:', err);
        }
    };

    // Handle Monaco editor mount - set up autocomplete
    const handleEditorDidMount = useCallback((editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;

        // Dispose of any existing completion provider
        if (completionProviderRef.current) {
            completionProviderRef.current.dispose();
        }

        // Register completion provider for JSON
        completionProviderRef.current = monaco.languages.registerCompletionItemProvider('json', {
            triggerCharacters: ['"', ':'],
            provideCompletionItems: (model, position) => {
                const textUntilPosition = model.getValueInRange({
                    startLineNumber: position.lineNumber,
                    startColumn: 1,
                    endLineNumber: position.lineNumber,
                    endColumn: position.column,
                });

                // Check if we're typing a value for companyId
                const companyIdMatch = textUntilPosition.match(/"companyId"\s*:\s*"([^"]*)$/i);
                if (companyIdMatch) {
                    const filterText = companyIdMatch[1].toLowerCase();
                    return {
                        suggestions: companiesRef.current
                            .filter(c => !filterText || c.name.toLowerCase().includes(filterText) || c.id.toLowerCase().includes(filterText))
                            .map(company => ({
                                label: `${company.name}`,
                                kind: monaco.languages.CompletionItemKind.Value,
                                insertText: company.id,
                                detail: `ID: ${company.id.slice(0, 8)}...`,
                                documentation: `Company: ${company.name}\nSize: ${company.size}\nID: ${company.id}`,
                                sortText: company.name,
                            })),
                    };
                }

                // Check if we're typing a value for userId
                const userIdMatch = textUntilPosition.match(/"userId"\s*:\s*"([^"]*)$/i);
                if (userIdMatch) {
                    const filterText = userIdMatch[1].toLowerCase();
                    return {
                        suggestions: usersRef.current
                            .filter(u => !filterText ||
                                u.email.toLowerCase().includes(filterText) ||
                                u.firstName?.toLowerCase().includes(filterText) ||
                                u.lastName?.toLowerCase().includes(filterText) ||
                                u.id.toLowerCase().includes(filterText))
                            .map(user => ({
                                label: `${user.firstName || ''} ${user.lastName || ''} (${user.email})`,
                                kind: monaco.languages.CompletionItemKind.Value,
                                insertText: user.id,
                                detail: `ID: ${user.id.slice(0, 8)}...`,
                                documentation: `User: ${user.firstName} ${user.lastName}\nEmail: ${user.email}\nID: ${user.id}`,
                                sortText: user.email,
                            })),
                    };
                }

                return { suggestions: [] };
            },
        });
    }, []);

    // Cleanup completion provider on unmount
    useEffect(() => {
        return () => {
            if (completionProviderRef.current) {
                completionProviderRef.current.dispose();
            }
        };
    }, []);

    // Filter job types by category
    const filteredJobTypes = useMemo(() => {
        if (activeTab === 'all') {
            return jobTypes;
        }
        return jobTypesByCategory[activeTab] || [];
    }, [jobTypes, jobTypesByCategory, activeTab]);

    // Get selected job type details
    const selectedJobType = useMemo(() => {
        return getJobType(formData.type);
    }, [formData.type, getJobType]);

    // Procurement categories for TED queries
    const procurementCategories = {
        'agfo': 'Agriculture and Food',
        'comp': 'Computer and Related Services',
        'core': 'Construction and Real Estate',
        'dese': 'Defence and security',
        'educ': 'Education',
        'ener': 'Energy and Related Services',
        'ensa': 'Environment and Sanitation',
        'fina': 'Finance and Related Services',
        'mapr': 'Materials and Products',
        'mior': 'Mining and Ores',
        'serv': 'Other Services',
        'prpu': 'Printing and Publishing',
        'reco': 'Research and Development',
        'teeq': 'Technology and Equipment',
        'tran': 'Transport and Related Services'
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!selectedJobType) {
            setError('Please select a job type');
            return;
        }

        // Validation based on schema-derived requirements
        if (selectedJobType.requiresCompany && !selectedCompanyId) {
            setError('Company selection is required for this job type');
            return;
        }
        if (selectedJobType.requiresTenderId && !formData.tenderId) {
            setError('Tender ID is required for this job type');
            return;
        }
        if (selectedJobType.requiresDocumentId && !formData.documentId) {
            setError('Document ID is required for this job type');
            return;
        }

        // Validate parameters JSON if provided
        if (formData.parameters.trim() && formData.parameters !== '{}') {
            try {
                JSON.parse(formData.parameters);
            } catch {
                setError('Invalid JSON in parameters field');
                return;
            }
        }

        try {
            // Only carry the cron when the Scheduled toggle is on; a leftover
            // value from a toggled-off state must not schedule anything.
            const cron = isScheduled ? formData.cronSchedule.trim() : '';
            const effectiveForm = { ...formData, cronSchedule: cron };

            // Scheduled runs of types whose dedicated mutation can't carry a
            // cron are re-routed through the generic enqueue (see hook).
            const mutationKey = resolveMutationKey(selectedJobType, cron);
            const mutation = mutations[mutationKey];
            if (!mutation) {
                setError(
                    mutationKey
                        ? `Mutation '${mutationKey}' is not registered in GraphQLProvider — frontend wiring bug.`
                        : `Job type '${selectedJobType.key}' has no admin mutation mapped in JOB_TYPE_TO_MUTATION.`
                );
                return;
            }

            // Build variables using schema-based function
            const variables = buildVariables(formData.type, effectiveForm, selectedCompanyId);
            const result = await executeMutation(mutation, variables);

            // Check result using the mutation key
            if (result[mutationKey]?.success) {
                // Save params to history on success
                if (formData.parameters && formData.parameters !== '{}') {
                    saveParamsToHistory(formData.type, formData.parameters);
                }
                onJobCreated?.(result[mutationKey]);
                onClose();
                resetForm();
            } else {
                setError(result[mutationKey]?.error || 'Failed to create job');
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const resetForm = () => {
        setFormData({ type: '', tenderId: '', documentId: '', parameters: '{}', cronSchedule: '' });
        setIsScheduled(false);
    };

    // Handle job type selection change
    const handleJobTypeChange = (e) => {
        const newType = e.target.value;
        setShowHistoryDropdown(false); // Close history dropdown when changing job type
        setFormData(prev => ({
            ...prev,
            type: newType,
            parameters: getParametersPlaceholder(newType)
        }));
    };

    // Close history dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showHistoryDropdown && !e.target.closest('[data-history-dropdown]')) {
                setShowHistoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showHistoryDropdown]);

    // Get available categories from schema
    const availableCategories = useMemo(() => {
        const cats = new Set(jobTypes.map(jt => jt.category));
        return ['all', ...Array.from(cats)];
    }, [jobTypes]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create Job" size="lg">
            <div className="space-y-4">
                {/* Schema loading/error state */}
                {schemasLoading && (
                    <div className="bg-gray-800 border border-gray-700 p-3 flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                        <span className="text-sm text-gray-400">Loading job schemas...</span>
                    </div>
                )}

                {schemasError && (
                    <div className="bg-red-50 border border-red-200 p-3 flex items-center gap-2">
                        <ExclamationTriangleIcon className="w-5 h-5 text-red-700" />
                        <span className="text-sm text-red-700">Failed to load job schemas: {schemasError}</span>
                    </div>
                )}

                {/* Category Tabs */}
                <div className="flex gap-0 border border-gray-700">
                    {availableCategories.map(cat => (
                        <button
                            key={cat}
                            type="button"
                            onClick={() => setActiveTab(cat)}
                            className={`flex-1 px-3 py-1.5 font-mono text-xs font-bold uppercase transition-colors border-r border-gray-700 last:border-r-0 ${
 activeTab === cat
 ? 'bg-primary text-on-accent'
 : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
 }`}
                        >
                            {categories[cat]?.label || cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Job Type Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Job Type *
                        </label>
                        <select
                            value={formData.type}
                            onChange={handleJobTypeChange}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            required
                            disabled={schemasLoading}
                        >
                            <option value="">Select Job Type</option>
                            {filteredJobTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        {selectedJobType && (
                            <p className="text-xs text-gray-400 mt-1">
                                {selectedJobType.description}
                            </p>
                        )}
                        {selectedJobType?.usesGenericEnqueue && (
                            <p className="text-xs text-gray-500 mt-1">
                                No dedicated mutation for this type — triggered via the generic job enqueue. Parameters are passed through as entered.
                            </p>
                        )}
                    </div>

                    {/* Company Requirement Warning */}
                    {selectedJobType?.requiresCompany && (
                        <div className="bg-yellow-50 border border-yellow-200 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-yellow-700 text-sm font-medium">Company Required for Tenant Isolation</span>
                            </div>
                            <select
                                value={selectedCompanyId}
                                onChange={(e) => setSelectedCompanyId(e.target.value)}
                                className="w-full bg-gray-700 border border-yellow-200 text-white p-2 text-sm"
                            >
                                <option value="">-- Select Company --</option>
                                {companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Optional Company */}
                    {selectedJobType?.optionalCompany && !selectedJobType?.requiresCompany && (
                        <div className="bg-gray-800 border border-gray-700 p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-gray-300 text-sm font-medium">Company Filter (Optional)</span>
                            </div>
                            <select
                                value={selectedCompanyId}
                                onChange={(e) => setSelectedCompanyId(e.target.value)}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2 text-sm"
                            >
                                <option value="">All Companies</option>
                                {companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Leave as &quot;All Companies&quot; to process across all tenants</p>
                        </div>
                    )}

                    {/* Tender ID - show if job has tenderId param */}
                    {selectedJobType?.hasTenderId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Tender ID {selectedJobType.requiresTenderId && <span className="text-red-700">*</span>}
                            </label>
                            <input
                                type="text"
                                value={formData.tenderId}
                                onChange={(e) => setFormData(prev => ({ ...prev, tenderId: e.target.value }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                placeholder="UUID of the tender"
                                required={selectedJobType.requiresTenderId}
                            />
                        </div>
                    )}

                    {/* Document ID - show if job has documentId param */}
                    {selectedJobType?.hasDocumentId && (
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Document ID {selectedJobType.requiresDocumentId && <span className="text-red-700">*</span>}
                            </label>
                            <input
                                type="text"
                                value={formData.documentId}
                                onChange={(e) => setFormData(prev => ({ ...prev, documentId: e.target.value }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                placeholder="UUID of the document"
                                required={selectedJobType.requiresDocumentId}
                            />
                        </div>
                    )}

                    {/* Parameters - Monaco Editor with autocomplete */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-300">
                                Parameters (JSON)
                            </label>
                            <span className="text-xs text-blue-700">
                                Type &quot;companyId&quot;: &quot; or &quot;userId&quot;: &quot; for autocomplete
                            </span>
                        </div>

                        {/* Quick Actions Bar */}
                        {selectedJobType && (
                            <div className="flex items-center gap-2 mb-2">
                                {/* Fill Template Button */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        const template = getFullParameterTemplate(selectedJobType.key);
                                        if (template && template !== '{}') {
                                            setFormData(prev => ({ ...prev, parameters: template }));
                                        }
                                    }}
                                    className="flex items-center gap-1 px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs font-medium border border-gray-600"
                                    title="Fill with all available parameters and their default values"
                                >
                                    <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                                    Fill Template
                                </button>

                                {/* History Dropdown */}
                                {(() => {
                                    const history = getParamsHistoryForJob(selectedJobType.key);
                                    if (history.length === 0) return null;

                                    return (
                                        <div className="relative" data-history-dropdown>
                                            <button
                                                type="button"
                                                onClick={() => setShowHistoryDropdown(!showHistoryDropdown)}
                                                className="flex items-center gap-1 px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200"
                                            >
                                                <ClockIcon className="w-3.5 h-3.5" />
                                                History ({history.length})
                                                <ChevronDownIcon className={`w-3 h-3 transition-transform ${showHistoryDropdown ? 'rotate-180' : ''}`} />
                                            </button>

                                            {showHistoryDropdown && (
                                                <div className="absolute z-50 top-full left-0 mt-1 w-72 bg-gray-800 border border-gray-600 shadow-lg max-h-64 overflow-y-auto">
                                                    <div className="p-2 border-b border-gray-700 flex items-center justify-between">
                                                        <span className="text-xs text-gray-400 font-medium">Recent Parameters</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                clearParamsHistoryForJob(selectedJobType.key);
                                                                setShowHistoryDropdown(false);
                                                            }}
                                                            className="text-red-700 hover:text-red-700 text-xs flex items-center gap-1"
                                                        >
                                                            <TrashIcon className="w-3 h-3" />
                                                            Clear
                                                        </button>
                                                    </div>
                                                    {history.map((entry, idx) => {
                                                        // Format the timestamp
                                                        const date = new Date(entry.timestamp);
                                                        const timeAgo = (() => {
                                                            const diffMs = Date.now() - date.getTime();
                                                            const diffMins = Math.floor(diffMs / 60000);
                                                            if (diffMins < 1) return 'just now';
                                                            if (diffMins < 60) return `${diffMins}m ago`;
                                                            const diffHours = Math.floor(diffMins / 60);
                                                            if (diffHours < 24) return `${diffHours}h ago`;
                                                            const diffDays = Math.floor(diffHours / 24);
                                                            return `${diffDays}d ago`;
                                                        })();

                                                        // Preview the params (truncated)
                                                        let preview = entry.params;
                                                        try {
                                                            const parsed = JSON.parse(entry.params);
                                                            const keys = Object.keys(parsed);
                                                            preview = keys.slice(0, 3).map(k => `${k}: ${JSON.stringify(parsed[k]).slice(0, 15)}`).join(', ');
                                                            if (keys.length > 3) preview += '...';
                                                        } catch { /* use raw */ }

                                                        return (
                                                            <button
                                                                key={idx}
                                                                type="button"
                                                                onClick={() => {
                                                                    setFormData(prev => ({ ...prev, parameters: entry.params }));
                                                                    setShowHistoryDropdown(false);
                                                                }}
                                                                className="w-full text-left px-3 py-2 hover:bg-gray-700 border-b border-gray-700 last:border-b-0"
                                                            >
                                                                <div className="flex items-center justify-between mb-1">
                                                                    <span className="text-xs text-gray-500">{timeAgo}</span>
                                                                </div>
                                                                <div className="text-xs text-gray-300 font-mono truncate">
                                                                    {preview}
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        <div className="border border-gray-600 overflow-hidden">
                            <Editor
                                height="150px"
                                language="json"
                                theme="vs-dark"
                                value={formData.parameters}
                                onChange={(value) => setFormData(prev => ({ ...prev, parameters: value || '{}' }))}
                                onMount={handleEditorDidMount}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 12,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                    lineNumbers: 'off',
                                    scrollBeyondLastLine: false,
                                    wordWrap: 'on',
                                    tabSize: 2,
                                    insertSpaces: true,
                                    automaticLayout: true,
                                    padding: { top: 8, bottom: 8 },
                                    scrollbar: {
                                        vertical: 'auto',
                                        horizontal: 'hidden',
                                    },
                                    folding: false,
                                    lineDecorationsWidth: 0,
                                    lineNumbersMinChars: 0,
                                    glyphMargin: false,
                                    suggestOnTriggerCharacters: true,
                                    quickSuggestions: true,
                                }}
                            />
                        </div>
                        {/* Parameter hints from schema */}
                        {selectedJobType?.params?.length > 0 && (
                            <div className="mt-2 p-2 bg-gray-800 border border-gray-700 text-xs">
                                <div className="flex items-center gap-1 text-gray-400 mb-1">
                                    <InformationCircleIcon className="w-3 h-3" />
                                    <span>Available parameters:</span>
                                </div>
                                <div className="grid grid-cols-2 gap-1">
                                    {selectedJobType.params
                                        .filter(p => !['tenderId', 'documentId', 'companyId'].includes(p.jsonName))
                                        .slice(0, 8)
                                        .map(param => (
                                            <div key={param.jsonName} className="flex items-center gap-1">
                                                <code className={`text-${param.required ? 'yellow' : 'gray'}-400`}>
                                                    {param.jsonName}
                                                </code>
                                                <span className="text-gray-500">({goTypeToJSType(param.type)})</span>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Schedule Section */}
                    <div className="border-t border-gray-700 pt-4">
                        <div className="flex items-center justify-between mb-3">
                            <label className="block text-sm font-medium text-gray-300">
                                Schedule (Optional)
                            </label>
                            <button
                                type="button"
                                onClick={() => setIsScheduled(!isScheduled)}
                                className={`px-3 py-1 font-mono text-xs font-bold uppercase ${
 isScheduled
 ? 'bg-primary text-on-accent'
 : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
 }`}
                            >
                                {isScheduled ? 'SCHEDULED' : 'ONE-TIME'}
                            </button>
                        </div>

                        {isScheduled && (
                            <>
                                <input
                                    type="text"
                                    value={formData.cronSchedule}
                                    onChange={(e) => setFormData(prev => ({ ...prev, cronSchedule: e.target.value }))}
                                    className="w-full bg-gray-700 border border-gray-600 text-white p-2 font-mono"
                                    placeholder="0 0 * * *"
                                    required={isScheduled}
                                />
                                <div className="text-xs text-gray-400 mt-2 grid grid-cols-2 gap-1">
                                    <span><code>0 6 * * *</code> Daily 6 AM</span>
                                    <span><code>0 */6 * * *</code> Every 6h</span>
                                    <span><code>0 0 * * 1</code> Weekly Mon</span>
                                    <span><code>0 0 1 * *</code> Monthly</span>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Common TED Queries (for CHECK_NEW) */}
                    {formData.type === 'CHECK_NEW' && (
                        <div className="bg-gray-800 border border-gray-700 p-3">
                            <p className="font-mono text-xs font-bold text-gray-300 mb-2 uppercase">Quick TED Queries</p>
                            <div className="flex flex-wrap gap-1">
                                {Object.entries(procurementCategories).slice(0, 8).map(([key]) => (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setFormData(prev => ({
                                            ...prev,
                                            parameters: JSON.stringify({
                                                saveTenders: true,
                                                deepCheck: true,
                                                source: "TED",
                                                query: `(buyer-country IN (DEU)) AND (PC IN (${key}))`,
                                                daysBack: 1,
                                                daysAhead: 90,
                                                limit: 250,
                                                maxResults: 0,
                                            }, null, 2)
                                        }))}
                                        className="px-2 py-1 font-mono text-xs bg-gray-700 hover:bg-gray-600 text-blue-700"
                                    >
                                        {key.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 p-3">
                            <p className="font-mono text-xs text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4">
                        <button
                            type="button"
                            onClick={() => { onClose(); resetForm(); }}
                            className="px-4 py-2 font-mono text-xs font-bold uppercase bg-gray-800 text-gray-400 border border-gray-700 hover:bg-gray-700 hover:text-white"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || !formData.type}
                            className="px-4 py-2 font-mono text-xs font-bold uppercase bg-primary text-on-accent hover:brightness-110 disabled:opacity-50"
                        >
                            {loading ? 'CREATING...' : 'CREATE JOB'}
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default CreateJobModal;
